import { apiGet, apiPost, apiDelete } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// LLM model catalog (ai.AIModel) — drives the Models page in Agent Studio.
//
// Every "reading" and "conflict" string on these types is composed SERVER-SIDE
// in OO.Domain.AIInfo.ModelRegistryRules. The page renders them; it never
// composes its own sentence about what a column means. That keeps Agent Studio,
// any future CLI and any report saying the same thing about the same row.
//
// Two adoption numbers, two different questions — do not collapse them:
//   agentsWired  → ai.AiAgent.ModelId FK count. Configured intent.
//   traffic30d   → messages tagged with this model. Actual consumption.
//
// Pricing is NOT a column on ai.AIModel and should not become one: rates are an
// audit trail (ai.PromptLog.CostModelPricingId points at the exact row used at
// call time), they carry effective/expiry dates, and a model can have traffic
// without any registry row. The catalog joins the CURRENT rate on the wire.
// ─────────────────────────────────────────────────────────────────────────────

export type ModelConflictSeverity = 'Advisory' | 'Silent' | 'Blocking';

export interface ModelConflict {
  /** Stable machine key, e.g. TEMPERATURE_STRIPPED. */
  code: string;
  severity: ModelConflictSeverity;
  /** Chip-length. No trailing period. */
  headline: string;
  /** Names the runtime seam that swallows the setting. */
  detail: string;
  /** Column to change to clear it. */
  field: string;
}

/** Plain-language reading of one stored column. */
export interface ModelFieldExplanation {
  field: string;
  label: string;
  /** The literal cell value, so the page reconciles against SSMS. */
  raw: string;
  /** What that value MEANS at runtime. */
  reading: string;
  consequence?: string | null;
}

/** What an `apiId` actually points at. */
export interface ApiRef {
  id: number;
  title?: string | null;
  host?: string | null;
  apiVersion?: string | null;
  useFoundryAgent: boolean;
  useResponsesApi: boolean;
  /** Decided from the endpoint host, never the free-text title. */
  isAzureHosted: boolean;
}

export interface PricingRef {
  id: number;
  inputPer1MTokens: number;
  outputPer1MTokens: number;
  cachedInputPer1MTokens?: number | null;
  reasoningOutputPer1MTokens?: number | null;
  effectiveDate: string;
  /** 'Name' or 'DeploymentName' — which key matched the rate row. */
  matchedOn: string;
}

export interface RetirementRef {
  retirementDate: string;
  daysUntil: number;
  severity: 'None' | 'Informational' | 'Warning' | 'Critical';
}

export interface ModelRow {
  id: number;
  name: string;
  title?: string | null;
  deploymentName?: string | null;

  developmentCompanyId: number;
  provider?: string | null;

  apiId: number;
  api?: ApiRef | null;

  merchantId?: number | null;
  active: boolean;
  enableInChat: boolean;
  enableInProcess: boolean;

  maxContextTokens?: number | null;
  supportsVision: boolean;
  supportsReasoningEffort: boolean;
  supportsTemperature: boolean;
  toolDescriptionMaxLength?: number | null;
  reasoningEffortLevel?: string | null;
  temperature?: number | null;
  topP?: number | null;
  defaultProcessAssistantId?: string | null;

  /**
   * Denormalized display copy of the rate, stored on the registry row itself.
   * Null = no rate on file. `pricing` below carries the LIVE rate that costing
   * actually uses — when the two disagree the row carries a RATE_COPY_STALE
   * conflict, so never render these two as authoritative on their own.
   */
  inputPer1MTokens?: number | null;
  outputPer1MTokens?: number | null;

  /** Agents pinned via ai.AiAgent.ModelId. Configured intent. */
  agentsWired: number;
  /** …of which are Active (StatusId = 2). */
  agentsWiredActive: number;
  /** Messages tagged with this model in the last 30 days. Actual use. */
  traffic30d: number;

  pricing?: PricingRef | null;
  retirement?: RetirementRef | null;

  conflicts: ModelConflict[];
  explanations: ModelFieldExplanation[];

  createdDate?: string | null;
  lastUpdatedDate?: string | null;
}

export interface UnpricedModel {
  modelName: string;
  calls30d: number;
  unpricedCalls30d: number;
  /** False when no ai.AIModel row carries this name or deployment slug. */
  inRegistry: boolean;
  /**
   * True when a rate already exists. Unpriced calls then mean the calls ran
   * BEFORE it took effect — nothing to fix, because rates are forward-only.
   * Never offer "set a rate" when this is true.
   */
  hasCurrentRate: boolean;
  rateEffectiveDate?: string | null;
}

export interface TenantGap {
  merchantId: number;
  rows: number;
}

export interface RegistryHealth {
  totalRows: number;
  families: number;
  activeRows: number;
  rowsWithConflicts: number;
  calls30d: number;
  unpricedCalls30d: number;
  unpriced: UnpricedModel[];
  tenantsWithNoActiveModel: TenantGap[];
}

export interface ProviderRow {
  id: number;
  name: string;
  aIName?: string | null;
}

export interface AdoptionDay {
  day: string;
  count: number;
}

export interface AdoptionResponse {
  modelId: number;
  since: string;
  total: number;
  byDay: AdoptionDay[];
}

export interface ModelSearchParams {
  search?: string;
  activeOnly?: boolean;
  merchantId?: number;
  includeGlobal?: boolean;
}

export async function listModels(params: ModelSearchParams = {}): Promise<ModelRow[]> {
  const wrapped = await apiGet<OperationResult<ModelRow[]>>('/api/AIModel/Search', params);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load models');
  return wrapped.data ?? [];
}

export async function getModelHealth(): Promise<RegistryHealth> {
  const wrapped = await apiGet<OperationResult<RegistryHealth>>('/api/AIModel/Health');
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load registry health');
  return wrapped.data;
}

export async function getModelById(id: number): Promise<ModelRow> {
  const wrapped = await apiGet<OperationResult<ModelRow>>(`/api/AIModel/GetById/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load model');
  return wrapped.data;
}

/** Create. Field shape mirrors the AIModel domain entity. */
export async function addModel(model: Partial<ModelRow>): Promise<ModelRow> {
  const wrapped = await apiPost<OperationResult<ModelRow>>('/api/AIModel/Add', model);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to create model');
  return wrapped.data;
}

export async function updateModel(model: Partial<ModelRow> & { id: number }): Promise<ModelRow> {
  const wrapped = await apiPost<OperationResult<ModelRow>>('/api/AIModel/Update', model);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to update model');
  return wrapped.data;
}

export async function removeModel(id: number): Promise<void> {
  const wrapped = await apiDelete<OperationResult<unknown>>(`/api/AIModel/Remove/${id}`);
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to delete model');
}

export async function getModelAdoption(id: number): Promise<AdoptionResponse> {
  const wrapped = await apiGet<OperationResult<AdoptionResponse>>(`/api/AIModel/Adoption/${id}`);
  if (!wrapped.isSuccess || !wrapped.data) throw new Error(wrapped.message || 'Failed to load adoption');
  return wrapped.data;
}

export async function listProviders(): Promise<ProviderRow[]> {
  const wrapped = await apiGet<OperationResult<ProviderRow[]>>('/api/AIModel/Providers');
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load providers');
  return wrapped.data ?? [];
}

export async function listEndpoints(): Promise<ApiRef[]> {
  const wrapped = await apiGet<OperationResult<ApiRef[]>>('/api/AIModel/Endpoints');
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load endpoints');
  return wrapped.data ?? [];
}
